/**
 * React-Query hooks for the district-level IPAWS origination config
 * (ADR-139 D4).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createIpawsConfig,
  getIpawsLastProbe,
  listIpawsConfigs,
  testIpawsConnection,
  updateIpawsConfig,
  type IpawsConfigWrite,
} from '../../services/ipaws.service';

export const ipawsConfigKeys = {
  all: ['ipawsConfig'] as const,
  list: () => [...ipawsConfigKeys.all, 'list'] as const,
  lastProbes: () => [...ipawsConfigKeys.all, 'lastProbe'] as const,
  lastProbe: (district: number | null) =>
    [...ipawsConfigKeys.lastProbes(), district] as const,
};

/**
 * The caller's visible IPAWS configs. Server-side reads are org-scoped, so a
 * district admin gets at most their own org's row.
 */
export function useIpawsConfigs(enabled = true) {
  return useQuery({
    queryKey: ipawsConfigKeys.list(),
    queryFn: listIpawsConfigs,
    enabled,
  });
}

export function useCreateIpawsConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: IpawsConfigWrite) => createIpawsConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ipawsConfigKeys.all });
    },
  });
}

export function useUpdateIpawsConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: Partial<IpawsConfigWrite>;
    }) => updateIpawsConfig(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ipawsConfigKeys.all });
    },
  });
}

/**
 * Dispatch the IPAWS connectivity probe. The probe
 * never mutates the config itself, but its getAck outcome eventually lands
 * on the last-probe read surface, so a successful dispatch
 * invalidates that key for an immediate refetch — the caller keeps a short
 * poll window open for the asynchronous provider report-back.
 */
export function useTestIpawsConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (district?: number) => testIpawsConnection(district),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ipawsConfigKeys.lastProbes() });
    },
  });
}

/**
 * Latest persisted connectivity-probe outcome for a district's config
 * (`GET /api/v2/ipaws-config/last-probe/`).
 *
 * The test-connection transport is fire-and-forget, so the outcome lands
 * asynchronously: while `pollUntil` is in the future (the short window the
 * page opens after a dispatch), the query refetches every 5s — the same
 * function-form `refetchInterval` idiom as `useSiteFloorplan`'s processing
 * poll. Outside the window it is a plain one-shot read.
 */
export function useIpawsLastProbe(
  district: number | null,
  {
    enabled = true,
    pollUntil = null,
  }: { enabled?: boolean; pollUntil?: number | null } = {},
) {
  return useQuery({
    queryKey: ipawsConfigKeys.lastProbe(district),
    queryFn: () => getIpawsLastProbe(district ?? undefined),
    enabled: enabled && district != null,
    refetchInterval: () =>
      pollUntil != null && Date.now() < pollUntil ? 5_000 : false,
  });
}
